import React, { useContext, useState } from "react";
import { useFormik } from "formik";
import { AuthContext } from "../context/AuthProvider"; // Ensure path is correct
import RegisSchema from "./RegisSchema"; // Ensure path is correct and schema exists
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { FiUserPlus,  FiEye, FiEyeOff } from "react-icons/fi"; // Icons

// import './Register.css'; // Custom CSS can be in a separate file or below

const Register = () => {
  const { register } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const gradientColor = "linear-gradient(135deg, #e5272e 0%, #f59332 100%)";
  const primaryAccentColor = "#e5272e"; // Red from gradient

  const formik = useFormik({
    initialValues: {
      fullName: "",
      mobile: "", // Changed from 'phone' to 'mobile' to match formik values
      email: "",
      password: "",
    },
    validationSchema: RegisSchema, // Make sure RegisSchema expects 'fullName' and 'mobile'
    onSubmit: async (values, actions) => {
      setLoading(true);
      try {
        // Ensure the values sent to the register function match its expected parameters
        // If your register function expects 'fullname', adjust here or in the function
        const payload = {
            fullname: values.fullName, // API might expect 'fullname'
            phone: values.mobile,    // API might expect 'phone'
            email: values.email,
            password: values.password,
        };
        const res = await register(payload); // Pass the potentially transformed payload

        if (res.success) {
          toast.success("Registration successful! Please login.");
          actions.resetForm();
          navigate('/login');
        } else {
          toast.error(res.message || "Registration failed. Please try again.");
        }
      } catch (error) {
        console.error("Registration error:", error);
        toast.error(error.response?.data?.message || "Something went wrong during registration!");
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <>
      <style>
        {`
          .register-page-container {
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: #f4f7f9; /* Light gray background */
            padding: 2rem 0;
          }
          .register-card {
            background-color: #ffffff;
            border-radius: 16px;
            box-shadow: 0 12px 40px rgba(0, 0, 0, 0.1);
            overflow: hidden;
            display: flex;
            width: 100%;
            max-width: 950px; /* Max width for the card, slightly wider for more fields */
          }
          .register-form-section {
            padding: 2.5rem 3rem; 
            flex: 1 1 55%; 
            display: flex;
            flex-direction: column;
            justify-content: center;
          }
          .register-visual-section {
            flex: 1 1 45%; 
            background-image: ${gradientColor};
            background-size: 200% 200%; 
            animation: gradientAnimationRegister 10s ease infinite;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            color: white;
            padding: 2rem;
            text-align: center;
          }
          @keyframes gradientAnimationRegister { /* Unique animation name */
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          .register-visual-section h2 {
            font-weight: 700;
            font-size: 2.5rem;
            margin-bottom: 1rem;
            text-shadow: 1px 1px 3px rgba(0,0,0,0.2);
          }
          .register-visual-section p {
            font-size: 1.1rem;
            opacity: 0.9;
            max-width: 300px;
          }
          .register-title {
            font-size: 2rem;
            font-weight: 700;
            color: ${primaryAccentColor};
            margin-bottom: 0.5rem;
            display: flex;
            align-items: center;
          }
          .register-title svg {
            margin-right: 10px;
          }
          .register-subtitle {
            font-size: 1rem;
            color: #6c757d;
            margin-bottom: 1.5rem; /* Reduced margin */
          }
          .form-label-register {
            font-weight: 600;
            color: #343a40;
            font-size: 0.9rem;
          }
          .form-control-register {
            border-radius: 8px;
            border: 1px solid #ced4da;
            padding: 0.75rem 1rem;
            padding-left: 3rem; 
            font-size: 1rem;
            transition: border-color 0.2s ease, box-shadow 0.2s ease;
            width: 100%; 
          }
          .form-control-register:focus {
            border-color: ${primaryAccentColor};
            box-shadow: 0 0 0 0.2rem rgba(229, 39, 46, 0.2);
            background-color: #fff;
          }
          .input-group-register {
            position: relative;
            display: flex; 
            align-items: center; 
          }
          .input-group-register .input-icon {
            position: absolute;
            left: 16px; 
            top: 50%;
            transform: translateY(-50%);
            color: #adb5bd;
            font-size: 1.1rem;
            z-index: 2; 
          }
          .password-toggle-icon-register {
            position: absolute;
            right: 16px; 
            top: 50%;
            transform: translateY(-50%);
            color: #adb5bd;
            cursor: pointer;
            font-size: 1.2rem;
            z-index: 2;
          }
          .register-btn-submit {
            background-image: ${gradientColor};
            color: white;
            border: none;
            padding: 0.8rem 1.5rem;
            border-radius: 50px;
            font-weight: 600;
            font-size: 1.05rem;
            transition: opacity 0.2s ease, transform 0.15s ease, box-shadow 0.2s ease;
            width: 100%;
            margin-top: 1rem;
            box-shadow: 0 4px 12px rgba(0,0,0,0.1);
          }
          .register-btn-submit:hover {
            opacity: 0.85;
            transform: translateY(-2px);
            box-shadow: 0 6px 15px rgba(229, 39, 46, 0.25);
          }
          .register-btn-submit:disabled {
            opacity: 0.7;
            cursor: not-allowed;
          }
          .register-links {
            font-size: 0.9rem; /* Slightly larger */
          }
          .register-links a {
            color: ${primaryAccentColor};
            text-decoration: none;
            font-weight: 600; /* Bolder link */
          }
          .register-links a:hover {
            text-decoration: underline;
          }
          .error-message-register {
            color: #dc3545;
            font-size: 0.8rem;
            margin-top: 0.25rem;
          }

          /* Responsive adjustments */
          @media (max-width: 768px) {
            .register-card {
              flex-direction: column;
            }
            .register-visual-section {
              min-height: 200px; /* Reduced height for mobile */
              border-radius: 16px 16px 0 0; 
            }
            .register-form-section {
              padding: 2rem 1.5rem; 
            }
            .register-title {
              font-size: 1.8rem;
            }
          }
        `}
      </style>
      <section className="register-page-container">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-11 col-xl-10"> {/* Adjusted column for wider card */}
              <div className="register-card">
                <div className="register-form-section">
                  <h2 className="register-title">
                    <FiUserPlus /> Create Your Account
                  </h2>
                  <p className="register-subtitle">Join Booze247 for fast, anytime delivery!</p>
                  <form onSubmit={formik.handleSubmit} noValidate>
                    <div className="mb-3">
                      <label htmlFor="regFullName" className="form-label form-label-register">
                        Full Name
                      </label>
                      <div className="input-group-register">
                        
                        <input
                          type="text"
                          className={`form-control form-control-register ${formik.touched.fullName && formik.errors.fullName ? 'is-invalid' : ''}`}
                          id="regFullName"
                          name="fullName"
                          placeholder="Enter your full name"
                          value={formik.values.fullName}
                          onBlur={formik.handleBlur}
                          onChange={formik.handleChange}
                        />
                      </div>
                      {formik.touched.fullName && formik.errors.fullName && (
                        <p className="error-message-register">{formik.errors.fullName}</p>
                      )}
                    </div>

                    <div className="mb-3">
                      <label htmlFor="regMobile" className="form-label form-label-register">
                        Mobile Number
                      </label>
                       <div className="input-group-register">
                       
                        <input
                          type="tel" // Changed to tel for mobile numbers
                          className={`form-control form-control-register ${formik.touched.mobile && formik.errors.mobile ? 'is-invalid' : ''}`}
                          id="regMobile"
                          name="mobile"
                          placeholder="Enter your mobile number"
                          value={formik.values.mobile}
                          onBlur={formik.handleBlur}
                          onChange={formik.handleChange}
                        />
                      </div>
                      {formik.touched.mobile && formik.errors.mobile && (
                        <p className="error-message-register">{formik.errors.mobile}</p>
                      )}
                    </div>

                    <div className="mb-3">
                      <label htmlFor="regEmail" className="form-label form-label-register">
                        Email Address
                      </label>
                      <div className="input-group-register">
                       
                        <input
                          type="email"
                          className={`form-control form-control-register ${formik.touched.email && formik.errors.email ? 'is-invalid' : ''}`}
                          id="regEmail"
                          name="email"
                          placeholder="you@example.com"
                          value={formik.values.email}
                          onBlur={formik.handleBlur}
                          onChange={formik.handleChange}
                        />
                      </div>
                      {formik.touched.email && formik.errors.email && (
                        <p className="error-message-register">{formik.errors.email}</p>
                      )}
                    </div>

                    <div className="mb-4"> {/* Increased margin for password field */}
                      <label htmlFor="regPassword" className="form-label form-label-register">
                        Password
                      </label>
                      <div className="input-group-register">
                       
                        <input
                          type={showPassword ? "text" : "password"}
                          className={`form-control form-control-register ${formik.touched.password && formik.errors.password ? 'is-invalid' : ''}`}
                          id="regPassword"
                          name="password"
                          placeholder="Create a strong password"
                          value={formik.values.password}
                          onBlur={formik.handleBlur}
                          onChange={formik.handleChange}
                        />
                        <span onClick={() => setShowPassword(!showPassword)} className="password-toggle-icon-register">
                          {showPassword ? <FiEyeOff /> : <FiEye />}
                        </span>
                      </div>
                      {formik.touched.password && formik.errors.password && (
                        <p className="error-message-register">{formik.errors.password}</p>
                      )}
                    </div>

                    <button
                      type="submit"
                      className="btn register-btn-submit"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Registering...
                        </>
                      ) : (
                        "Create Account"
                      )}
                    </button>
                  </form>
                  <div className="text-center mt-4 register-links">
                    <span>Already have an account? </span>
                    <Link to="/login">Login Here</Link>
                  </div>
                </div>
                <div className="register-visual-section d-none d-md-flex">
                  <h2>Join Booze247</h2>
                  <p>Sign up to unlock exclusive deals and enjoy the fastest delivery service in town!</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Register;
